import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { createEffectFactory } from '@recruitee/ngrx';

import { EmailTemplatesAutoReplyHttpClient } from '../auto-reply-http-client';
import { EmailTemplatesAutoReplyCollectionActions } from '../collection/auto-reply-collection.store';
import { EmailTemplatesAutoReplyItemActions } from './auto-reply-item.store';

@Injectable({ providedIn: 'root' })
export class EmailTemplatesAutoReplyItemEffects {
  public effects = createEffectFactory(EmailTemplatesAutoReplyItemActions, this.actions$);

  public fetchItem$ = this.effects(EmailTemplatesAutoReplyItemActions.fetch, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.loadItem(payload),
  );

  public create$ = this.effects(EmailTemplatesAutoReplyItemActions.create, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.create(payload).pipe(
      map(data => EmailTemplatesAutoReplyItemActions.createSuccess({ payload: { data } })),
      catchError(error => of(EmailTemplatesAutoReplyItemActions.createFailed({ payload: error }))),
    ),
  );

  public update$ = this.effects(EmailTemplatesAutoReplyItemActions.update, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.update(payload),
  );

  public duplicate$ = this.effects(EmailTemplatesAutoReplyItemActions.duplicate, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.duplicate(payload).pipe(
      map(data => EmailTemplatesAutoReplyItemActions.duplicateSuccess({ payload: { data } })),
      catchError(error =>
        of(EmailTemplatesAutoReplyItemActions.duplicateFailed({ payload: error })),
      ),
    ),
  );

  public saveAsTemplate$ = this.effects(
    EmailTemplatesAutoReplyItemActions.saveAsTemplate,
    ({ payload }) => {
      const { title, offerId, translations } = payload;
      return this.emailTemplatesAutoReplyHttpClient
        .saveAsTemplate(title, offerId, translations)
        .pipe(
          map(data =>
            EmailTemplatesAutoReplyItemActions.saveAsTemplateSuccess({ payload: { data } }),
          ),
          catchError(error =>
            of(EmailTemplatesAutoReplyItemActions.saveAsTemplateFailed({ payload: error })),
          ),
        );
    },
  );

  public loadOfferTemplateItem$ = this.effects(
    EmailTemplatesAutoReplyItemActions.loadOfferTemplateItem,
    ({ payload }) => {
      const { offerId, templateId } = payload;
      return this.emailTemplatesAutoReplyHttpClient.loadOfferTemplateItem(offerId, templateId).pipe(
        map(data =>
          EmailTemplatesAutoReplyItemActions.loadOfferTemplateItemSuccess({ payload: { data } }),
        ),
        catchError(error =>
          of(EmailTemplatesAutoReplyItemActions.loadOfferTemplateItemFailed({ payload: error })),
        ),
      );
    },
  );

  public setDefault$ = this.effects(EmailTemplatesAutoReplyItemActions.setDefault, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.setDefault(payload).pipe(
      map(data => EmailTemplatesAutoReplyItemActions.setDefaultSuccess({ payload: { data } })),
      catchError(error =>
        of(EmailTemplatesAutoReplyItemActions.setDefaultFailed({ payload: error })),
      ),
    ),
  );

  public delete$ = this.effects(EmailTemplatesAutoReplyItemActions.deleteTemplate, ({ payload }) =>
    this.emailTemplatesAutoReplyHttpClient.delete(payload).pipe(
      map(data => EmailTemplatesAutoReplyItemActions.deleteTemplateSuccess({ payload: data })),
      catchError(error =>
        of(EmailTemplatesAutoReplyItemActions.deleteTemplateFailed({ payload: error })),
      ),
    ),
  );

  public assignTemplateToOffer$ = this.effects(
    EmailTemplatesAutoReplyItemActions.assignTemplateToOffer,
    ({ payload }) => {
      const { offerId, autoReplyTemplateId } = payload;
      return this.emailTemplatesAutoReplyHttpClient
        .assignTemplateToOffer(offerId, autoReplyTemplateId)
        .pipe(
          map(data =>
            EmailTemplatesAutoReplyItemActions.assignTemplateToOfferSuccess({ payload: { data } }),
          ),
          catchError(error =>
            of(EmailTemplatesAutoReplyItemActions.assignTemplateToOfferFailed({ payload: error })),
          ),
        );
    },
  );

  public createOrUpdateCustomTemplate$ = this.effects(
    EmailTemplatesAutoReplyItemActions.createOrUpdateCustomTemplate,
    ({ payload }) => {
      const {
        offerId,
        translation,
        templateAttachmentAssignments,
        sourceTemplateId,
        isCustomAssigned,
      } = payload;

      return this.emailTemplatesAutoReplyHttpClient
        .createOrUpdateCustomTemplate(
          offerId,
          translation,
          templateAttachmentAssignments,
          sourceTemplateId,
          isCustomAssigned,
        )
        .pipe(
          map(data =>
            EmailTemplatesAutoReplyItemActions.createOrUpdateCustomTemplateSuccess({
              payload: { data },
            }),
          ),
          catchError(error =>
            of(
              EmailTemplatesAutoReplyItemActions.createOrUpdateCustomTemplateFailed({
                payload: error,
              }),
            ),
          ),
        );
    },
  );

  public refetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        EmailTemplatesAutoReplyItemActions.createSuccess,
        EmailTemplatesAutoReplyItemActions.updateSuccess,
        EmailTemplatesAutoReplyItemActions.duplicateSuccess,
        EmailTemplatesAutoReplyItemActions.deleteTemplateSuccess,
        EmailTemplatesAutoReplyItemActions.setDefaultSuccess,
      ),
      map(() => EmailTemplatesAutoReplyCollectionActions.fetchCollection()),
    ),
  );

  constructor(
    private emailTemplatesAutoReplyHttpClient: EmailTemplatesAutoReplyHttpClient,
    private actions$: Actions,
  ) {}
}
